import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DownloadCloud, RefreshCw } from "lucide-react";

function isNewer(remote, local) {
  const a = String(remote || "").split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(local || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] || 0) > (b[i] || 0)) return true;
    if ((a[i] || 0) < (b[i] || 0)) return false;
  }
  return false;
}

function nativeInfo() {
  if (typeof window === "undefined") return null;
  if (window.tiroNativeVersion) return { platform: "apk", version: String(window.tiroNativeVersion) };
  if (window.tiro && window.tiro.appVersion) return { platform: "exe", version: String(window.tiro.appVersion) };
  return null;
}

export default function UpdateBanner() {
  const { settings } = useAuth();
  const [info] = useState(nativeInfo());
  const [upd, setUpd] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [progress, setProgress] = useState(-1); // -1 idle, 0..100 downloading

  const check = useCallback(async (manual = false) => {
    if (!info) { if (manual) toast.info("Cek update hanya di aplikasi APK/EXE."); return; }
    try {
      const r = await api.get("/app-update");
      const p = r.data?.[info.platform];
      if (p && p.version && isNewer(p.version, info.version)) {
        setUpd({ ...p, platform: info.platform }); setDismissed(false);
        if (manual) toast.success(`Update ${p.version} tersedia`);
      } else { setUpd(null); if (manual) toast.success("Aplikasi sudah versi terbaru."); }
    } catch { if (manual) toast.error("Gagal cek update"); }
  }, [info]);

  useEffect(() => { check(false); }, [check]);
  useEffect(() => {
    const h = () => check(true);
    window.addEventListener("tiro-check-update", h);
    return () => window.removeEventListener("tiro-check-update", h);
  }, [check]);

  // Native (EXE) posts download progress / done / error back to the webview.
  useEffect(() => {
    if (typeof window === "undefined" || !window.chrome?.webview?.addEventListener) return;
    const h = (e) => {
      const m = typeof e.data === "string" ? e.data : "";
      if (m.startsWith("appupdate:progress:")) setProgress(parseInt(m.slice(19), 10) || 0);
      else if (m === "appupdate:done") { setProgress(100); toast.success("Unduhan selesai, memasang…"); }
      else if (m === "appupdate:error") { setProgress(-1); toast.error("Gagal mengunduh update"); }
    };
    window.chrome.webview.addEventListener("message", h);
    return () => { try { window.chrome.webview.removeEventListener("message", h); } catch { } };
  }, []);

  if (!upd) return null;

  const href = upd.url ? (upd.url.startsWith("http") ? upd.url : `${process.env.REACT_APP_BACKEND_URL}${upd.url}`) : "";
  const doUpdate = () => {
    if (!href) { toast.error("Link update belum diset admin."); return; }
    // EXE: download in-app with progress, then auto-run the installer.
    if (upd.platform === "exe" && window.tiro && window.tiro.installUpdate) {
      setProgress(0);
      try { window.tiro.installUpdate(href); return; } catch (e) { setProgress(-1); }
    }
    if (upd.platform === "apk" && window.TiroNative && window.TiroNative.installUpdate) {
      try { window.TiroNative.installUpdate(href); toast.info("Mengunduh update…"); return; } catch (e) { /* fallback */ }
    }
    if (window.tiro && window.tiro.openExternal) window.tiro.openExternal(href);
    else window.open(href, "_blank");
  };

  const mandatory = !!upd.mandatory;
  if (dismissed && !mandatory) return null;
  const busy = progress >= 0;
  const logo = settings?.site_logo || "/trmd-logo.webp";

  return (
    <div data-testid="update-modal" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/12 bg-gradient-to-b from-[#15171c] to-[#0c0d11] shadow-2xl overflow-hidden">
        <div className="flex flex-col items-center text-center px-7 pt-8 pb-6">
          <img src={logo} alt="TRMD" className="h-12 w-auto max-w-[180px] object-contain mb-4" />
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#e52321] mb-1">
            <DownloadCloud className="h-4 w-4" /> Update Tersedia
          </div>
          <h2 className="font-head font-black text-2xl uppercase text-white leading-tight">Versi v{upd.version}</h2>
          {upd.notes && <p className="text-slate-400 text-sm mt-3 max-h-40 overflow-auto whitespace-pre-line leading-relaxed">{upd.notes}</p>}

          {busy && (
            <div className="w-full mt-5" data-testid="update-progress">
              <div className="h-2.5 w-full rounded-full bg-[#2a2a2a] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#e52321] to-[#ff6a3d] transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-xs text-slate-400 mt-2 font-mono">{progress >= 100 ? "Memasang…" : `Mengunduh… ${progress}%`}</div>
            </div>
          )}
        </div>

        {!busy && (
          <div className="flex gap-2 px-7 pb-7">
            {!mandatory && (
              <button data-testid="update-later-btn" onClick={() => setDismissed(true)}
                className="flex-1 h-11 rounded-xl bg-[#1f1f1f] hover:bg-[#2a2a2a] border border-white/10 text-slate-200 font-bold text-sm transition-colors">
                Nanti
              </button>
            )}
            <button data-testid="update-now-btn" onClick={doUpdate}
              className="flex-[1.4] h-11 rounded-xl bg-gradient-to-r from-[#e52321] to-[#ff5a2d] hover:brightness-110 text-white font-bold text-sm transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-[#e52321]/20">
              <DownloadCloud className="h-4 w-4" /> Update Sekarang
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function CheckUpdateButton() {
  if (typeof window !== "undefined" && !window.tiroNativeVersion && !(window.tiro && window.tiro.appVersion)) return null;
  return (
    <button data-testid="manual-check-update-btn" onClick={() => window.dispatchEvent(new Event("tiro-check-update"))}
      className="w-full mt-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold">
      <RefreshCw className="h-4 w-4" /> Cek Update
    </button>
  );
}
